import { message } from 'ant-design-vue';
import {
  CATALOG_SEARCH_SET,
  CATALOG_REQUEST,
  CATALOG_UPDATE,
  CATALOG_CLEAN,
  CATALOG_SELECTED_SET,
  CATALOG_DELETE,
  CATALOG_ADD,
  CATALOG_SORT_SET,
  CATALOG_FILTERS_SET,
  CATALOG_FILTERS_REMOVE,
  CATALOG_FILTERS_EDIT,
} from '../types';
import { http } from '../../services';
import { onSearch, cleanState } from '../../utils';
import { sortByDate, sortByString, sortByNumber } from '../../utils/sort';

const initState = {
  search: '',
  loading: false,
  catalog: [],
  selected: [],
  sortBy: 'date',
  sortType: 'date',
  sortDirection: 'asc',
  filters: [],
};

const state = () => ({ ...initState });

const mutations = {
  [CATALOG_SEARCH_SET](state, payload) {
    state.search = payload;
  },
  [CATALOG_REQUEST](state, payload = true) {
    state.loading = payload;
  },
  [CATALOG_UPDATE](state, payload) {
    state.catalog = [...payload];
  },
  [CATALOG_CLEAN](state) {
    cleanState(state, initState);
  },
  [CATALOG_SELECTED_SET](state, payload) {
    state.selected = [...payload];
  },
  [CATALOG_SORT_SET](state, { sortBy, sortDirection, sortType }) {
    state.sortBy = sortBy;
    state.sortDirection = sortDirection;
    state.sortType = sortType;
  },
  [CATALOG_FILTERS_SET](state, payload) {
    state.filters = [...payload];
  },
  [CATALOG_FILTERS_REMOVE](state, id) {
    const idx = state.filters.findIndex((filter) => filter.id === id);
    if (idx >= 0) {
      state.filters.splice(idx, 1);
    }
  },
  [CATALOG_FILTERS_EDIT](state, filter) {
    const idx = state.filters.findIndex(({ id }) => id === filter.id);
    if (idx >= 0) {
      state.filters.splice(idx, 1, { ...state.filters[idx], ...filter });
    }
  },
};

const actions = {
  [CATALOG_REQUEST]: async ({ commit }) => {
    commit(CATALOG_REQUEST);
    const data = await http.getCatalog();
    if (data) commit(CATALOG_UPDATE, data);
    commit(CATALOG_REQUEST, false);
  },
  [CATALOG_DELETE]: async ({ state, dispatch, commit }) => {
    const selectedIds = [...state.selected];
    const result = state.catalog.filter(({ id }) => selectedIds.every((item) => item !== id));
    commit(CATALOG_UPDATE, result);
    commit(CATALOG_SELECTED_SET, []);
    const oks = await Promise.all(
      selectedIds.map(async (id) => {
        const ok = await http.deleteVideo(id);
        return ok;
      }),
    );
    if (oks.some((ok) => !ok)) {
      commit(CATALOG_SELECTED_SET, selectedIds);
      dispatch(CATALOG_REQUEST);
      message.warn('Video(s) not deleted. Please try again later!');
    } else {
      message.success(`Video${selectedIds.length > 1 ? 's' : ''} deleted successfully!`);
    }
  },
  [CATALOG_ADD]: async ({ dispatch }, link) => {
    const data = await http.addVideo({ link });
    if (data) {
      dispatch(CATALOG_REQUEST);
      message.success('Video(s) uploaded successfully!');
      return true;
    }
    message.warn('Video(s) not uploaded. Please try again later!');
    return false;
  },
  [CATALOG_CLEAN]: async ({ commit }) => {
    commit(CATALOG_CLEAN);
  },
};
const getters = {
  getCatalog(state, getters, { folders: { selectedFolder = null, folders = [] } }) {
    const { catalog = [], search = '', sortBy, sortType, sortDirection } = state;
    const filtered = catalog
      .map((item) => ({
        ...item,
        folders: folders.filter(({ videos }) => (videos || []).some((id) => id === item.id)),
      }))
      .filter(onSearch(search))
      .filter((item) => {
        if (selectedFolder) {
          const folder = folders.find(({ id }) => selectedFolder === id);
          if (folder) {
            return (folder.videos || []).some((id) => id === item.id);
          }
        }
        return true;
      });
    const sorted = filtered.sort((a, b) => {
      switch (sortType) {
        case 'string':
          return sortByString(a?.[sortBy], b?.[sortBy], sortDirection);
        case 'date':
          return sortByDate(a?.[sortBy], b?.[sortBy], sortDirection);
        case 'number':
          return sortByNumber(a?.[sortBy], b?.[sortBy], sortDirection);
        default:
          return sortByNumber(a?.[sortBy], b?.[sortBy], sortDirection);
      }
    });
    return sorted;
  },
  getVideoNames: ({ catalog }) => catalog.map(({ id, name }) => ({ id, name })),
  isSearch: ({ search }) => !!search,
  isEmpty: ({ catalog, loading }) => !catalog.length && !loading,
  hasSelected: ({ selected }) => !!selected.length,
  getFilterById: ({ filters }) => (id) => filters.find((filter) => filter.id === id),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
